import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { BsTelephoneFill, BsChatDotsFill } from 'react-icons/bs';
import { formatDate, maskMobile } from '../../utils/subscriberHelpers';
import { initiateClickToCall, sendApplicationUrlSms } from '../../services/clickToCallService';
import CreditPermissionToggles from './CreditPermissionToggles';

const SubscriberInfoCard = ({ profile, showPhone, setShowPhone, isFinance, isSupport, isSuperAdmin, applicationId, currentRole, onNotification, creditSummary }) => {
  // Replaces the former "Wallet Activation" badge here — a different
  // concept (credit-line lending status, not wallet PIN activation).
  // "Activated" is a further stage past "Activation" (pre_approved -> active),
  // not a separate branch — once a subscriber is Activated, Activation also
  // reads Yes (it was necessarily completed to get there).
  const hasCreditLineAccount = !!creditSummary?.has_credit_line_account;
  const isCreditActivated = creditSummary?.activation_status === 'active';
  const isCreditActivation = isCreditActivated || creditSummary?.activation_status === 'pre_approved';

  const [calling, setCalling] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [showPan, setShowPan] = useState(false);
  const [showAadhar, setShowAadhar] = useState(false);

  const handleClickToCall = async () => {
    if (!applicationId || !currentRole) return;

    if (currentRole !== 'Support' && currentRole !== 'Ops') {
      if (onNotification) {
        onNotification('Access denied. Only Support and Ops roles can initiate calls.', 'error');
      }
      return;
    }

    setCalling(true);
    try {
      const response = await initiateClickToCall(currentRole, applicationId);
      const callerdesk = response?.msg?.callerdesk_response;

      if (callerdesk && callerdesk.type === 'error' && callerdesk.message) {
        if (onNotification) {
          onNotification(callerdesk.message, 'error');
        }
        return;
      }

      if (response && response.metadata && response.metadata.api_status === true) {
        if (onNotification) {
          const msg = typeof response.msg === 'string' ? response.msg : (response.msg?.message || 'Call initiated successfully');
          onNotification(`${msg} You will be connected shortly.`, 'success');
        }
      } else {
        throw new Error(response?.msg?.message || response?.msg || 'Failed to initiate call');
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to initiate call';
      if (onNotification) {
        onNotification(errorMessage, 'error');
      }
    } finally {
      setCalling(false);
    }
  };

  const handleSendSms = async () => {
    if (!applicationId || !currentRole) return;

    if (currentRole !== 'Support' && currentRole !== 'Ops') {
      if (onNotification) {
        onNotification('Access denied. Only Support and Ops roles can send SMS.', 'error');
      }
      return;
    }

    setSendingSms(true);
    try {
      const response = await sendApplicationUrlSms(currentRole, applicationId);

      if (response && response.metadata && response.metadata.api_status === true) {
        if (onNotification) {
          const msg = typeof response.msg === 'string' ? response.msg : (response.msg?.message || 'SMS sent successfully');
          onNotification(msg, 'success');
        }
      } else {
        throw new Error(response?.msg?.message || response?.msg || 'Failed to send SMS');
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to send SMS';
      if (onNotification) {
        onNotification(errorMessage, 'error');
      }
    } finally {
      setSendingSms(false);
    }
  };

  const maskedPhoneNumber = React.useMemo(() => {
    const phoneNumber = profile?.personal?.mobile || profile?.phoneFull;
    return maskMobile(phoneNumber);
  }, [profile?.personal?.mobile, profile?.phoneFull]);

  const displayName = profile?.personal?.full_name || profile?.name || 'Subscriber';
  const initial = displayName.charAt(0).toUpperCase();

  const DOT_TONE = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
  };

  const Pill = ({ tone, children }) => {
    const t = DOT_TONE[tone] || DOT_TONE.slate;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ring-black/5 ${t.bg} ${t.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
        {children}
      </span>
    );
  };

  const Field = ({ label, children }) => (
    <div className="rounded-xl bg-slate-50/70 border border-slate-100 px-3.5 py-2.5">
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{children}</div>
    </div>
  );

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      {/* Identity header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-teal-50 via-slate-50 to-white border-b border-slate-100 px-5 py-5 sm:px-6">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-teal-100/40 pointer-events-none" />
        <div className="relative flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-lg font-bold text-teal-700 shrink-0 ring-1 ring-slate-200 shadow-sm">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-lg font-bold text-gray-900 truncate">{displayName}</div>
            <div className="text-sm text-slate-500 flex items-center gap-2 mt-1 flex-wrap">
              <span>
                {
                  (isFinance || isSupport || isSuperAdmin)
                    ? (profile?.personal?.mobile || profile?.phoneFull)
                    : (showPhone ? (profile?.personal?.mobile || profile?.phoneFull) : maskedPhoneNumber)
                }
              </span>
              {!(isFinance || isSupport || isSuperAdmin) && (
                <button
                  onClick={() => setShowPhone(v => !v)}
                  className="text-slate-400 hover:text-slate-700 transition-colors"
                  aria-label={showPhone ? 'Hide phone' : 'Show phone'}
                >
                  {showPhone ? <FaEyeSlash /> : <FaEye />}
                </button>
              )}
            </div>
          </div>
          {((isSupport || currentRole === 'Ops') && (profile?.personal?.mobile || profile?.phoneFull)) && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleClickToCall}
                disabled={calling}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl transition-all ${
                  calling ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm shadow-emerald-500/20'
                }`}
                aria-label="Initiate call"
                title="Click to call subscriber"
              >
                <BsTelephoneFill className={calling ? 'animate-pulse' : ''} />
                <span>{calling ? 'Calling…' : 'Call'}</span>
              </button>
              <button
                onClick={handleSendSms}
                disabled={sendingSms}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl transition-all ${
                  sendingSms ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-700 hover:bg-slate-50 ring-1 ring-slate-200 shadow-sm'
                }`}
                aria-label="Send SMS"
                title="Send application URL SMS to subscriber"
              >
                <BsChatDotsFill className={sendingSms ? 'animate-pulse' : ''} />
                <span>{sendingSms ? 'Sending…' : 'SMS'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-5">
        {/* Field grid — every field is its own cell so the grid flows and
            balances naturally regardless of how many fields any group has. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Field label="Ref Code">{profile?.subscription || 'N/A'}</Field>
          <Field label="Plan">{profile?.plan || 'N/A'}</Field>
          <Field label="Validity Period">
            {(() => {
              const validPeriod = profile?.valid_period;
              if (!validPeriod) return 'N/A';
              if (typeof validPeriod === 'object' && validPeriod !== null && validPeriod.start_date && validPeriod.end_date) {
                return `${formatDate(validPeriod.start_date)} - ${formatDate(validPeriod.end_date)}`;
              }
              return formatDate(validPeriod);
            })()}
          </Field>
          <Field label="KYC Status">
            <Pill tone={profile?.kyc ? 'emerald' : 'slate'}>{profile?.kyc || 'N/A'}</Pill>
          </Field>
          <Field label="Status">
            <Pill tone={profile?.status === 'Active' ? 'emerald' : 'amber'}>{profile?.status || 'N/A'}</Pill>
          </Field>
          <Field label="Login Status">
            <div className="flex items-center gap-1.5">
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${
                profile?.login_count > 0 ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'
              }`}>
                {profile?.login_count > 0 ? '✓' : '—'}
              </span>
              <span className="text-sm font-semibold text-gray-900">{profile?.login_count ?? 0}</span>
            </div>
          </Field>
          {hasCreditLineAccount && (
            <>
              <Field label="Ayushpay Credit Activation">
                <Pill tone={isCreditActivation ? 'amber' : 'slate'}>{isCreditActivation ? 'Yes' : 'No'}</Pill>
              </Field>
              <Field label="Ayushpay Credit Activated">
                <Pill tone={isCreditActivated ? 'emerald' : 'slate'}>{isCreditActivated ? 'Yes' : 'No'}</Pill>
              </Field>
            </>
          )}
        </div>

        {/* PAN & Aadhaar — Ops / SuperAdmin only */}
        {(isSuperAdmin || currentRole === 'Ops') && (
          <div className="pt-1 border-t border-slate-100">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-4">Identity Documents</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-50/70 border border-slate-100 px-3.5 py-2.5">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">PAN No</div>
                <div className="text-sm font-semibold text-gray-900 flex items-center gap-2 font-mono">
                  <span>
                    {profile?.pan_number
                      ? (showPan ? profile.pan_number : 'XXXXXXXXXX')
                      : 'N/A'}
                  </span>
                  {profile?.pan_number && (
                    <button onClick={() => setShowPan(v => !v)} className="text-slate-400 hover:text-slate-600 transition-colors" aria-label={showPan ? 'Hide PAN' : 'Show PAN'}>
                      {showPan ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  )}
                </div>
              </div>
              <div className="rounded-xl bg-slate-50/70 border border-slate-100 px-3.5 py-2.5">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1">Aadhaar No</div>
                <div className="text-sm font-semibold text-gray-900 flex items-center gap-2 font-mono">
                  <span>
                    {profile?.aadhar_number
                      ? (showAadhar ? profile.aadhar_number : 'XXXX-XXXX-' + String(profile.aadhar_number).slice(-4))
                      : 'N/A'}
                  </span>
                  {profile?.aadhar_number && (
                    <button onClick={() => setShowAadhar(v => !v)} className="text-slate-400 hover:text-slate-600 transition-colors" aria-label={showAadhar ? 'Hide Aadhaar' : 'Show Aadhaar'}>
                      {showAadhar ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <CreditPermissionToggles
          isSuperAdmin={isSuperAdmin}
          hasCreditLineAccount={hasCreditLineAccount}
          applicationId={applicationId}
          currentRole={currentRole}
        />
      </div>
    </div>
  );
};

export default SubscriberInfoCard;
